/**
 * The list price as one sentence, from GET /api/public/pricing.
 *
 * The answer comes from the default plan the ops console sets
 * (api/app/Controllers/PublicPricingController.js), so nothing that shows a
 * price writes the number down. This is the tested original of the sentence;
 * ui/static/signup.html, which cannot import a module, carries the same few
 * lines word for word. marketing/index.html asks the same endpoint but lays
 * the figures out as a block rather than a sentence.
 *
 * Returns '' whenever there is nothing safe to say: not published, a failed
 * request, a malformed answer. A page then simply shows no price.
 */
export function priceLine(pricing) {
  const p = pricing || {};
  const whole = value => Number.isInteger(value) && value >= 0;
  if (p.published !== true) return '';
  if (!whole(p.base_price_cents) || !whole(p.included_users) || !whole(p.extra_user_cents)) {
    return '';
  }
  const dollars = cents => {
    const n = cents / 100;
    return `$${n % 1 === 0 ? String(n) : n.toFixed(2)}`;
  };
  let line =
    `${dollars(p.base_price_cents)} a month includes ${p.included_users} ` +
    `${p.included_users === 1 ? 'user' : 'users'}. ` +
    `Each additional user is ${dollars(p.extra_user_cents)} a month.`;
  if (whole(p.pilot_days) && p.pilot_days > 0) {
    line += ` Free for ${p.pilot_days} days, and no card needed to start.`;
  }
  return line;
}

/**
 * Where to ask. The demo is its own database with a seeded, meaningless rate
 * card, so on the demo host the price is asked of PRODUCTION (the endpoint
 * allows that origin); everywhere else the app asks its own API.
 */
export function pricingUrl(hostname) {
  return hostname === 'demo.ladeway.com'
    ? 'https://app.ladeway.com/api/public/pricing'
    : '/api/public/pricing';
}

/** The sentence, or '' on any failure. Never throws, never blocks a page. */
export async function fetchPriceLine({
  hostname = window.location.hostname,
  fetchImpl = window.fetch,
} = {}) {
  try {
    if (typeof fetchImpl !== 'function') return '';
    const res = await fetchImpl(pricingUrl(hostname), { credentials: 'omit' });
    if (!res || !res.ok) return '';
    return priceLine(await res.json());
  } catch (e) {
    return '';
  }
}
